import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent, LayoutFooter } from '@astryxdesign/core/Layout'
import { Stack, StackItem } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Token } from '@astryxdesign/core/Token'
import { Switch } from '@astryxdesign/core/Switch'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { TextInput } from '@astryxdesign/core/TextInput'
import { DateInput } from '@astryxdesign/core/DateInput'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { AlertDialog } from '@astryxdesign/core/AlertDialog'
import { VisuallyHidden } from '@astryxdesign/core/VisuallyHidden'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import { List, ListItem } from '@astryxdesign/core/List'
import { createMember, deleteMember, listMembers, searchPlayers, updateMember, validateMember } from './consoleApi'
import {
    ACCOUNT_EMAIL_NOTE,
    ADD_MODES,
    adminNote,
    birthDateText,
    canBeAdmin,
    isoDay,
    matchingMemberText,
    memberDraft,
    memberDraftErrors,
    memberFailureText,
    memberName,
    memberRoles,
    memberToCreate,
    memberToUpdate,
    membersTitle,
    pendingTitle,
    playerDraft,
    playerLine,
    refuseQuestion,
    removeQuestion,
    splitMembers,
    todayParts,
} from './consoleText'

// The club's members: the players asking to join it first, each accepted or
// refused, then the roster, each member edited or removed, and a way to add
// one by hand.
//
// The search goes to the server, a moment after the admin stops typing. A
// change reloads the list rather than patching it, because accepting a
// request can merge it into a roster entry the list already shows.
const SEARCH_DELAY_MS = 300

const fixed = width => ({ width, minWidth: width })
const columns = {
    email: fixed('calc(var(--spacing-10) * 6)'),
    phone: fixed('calc(var(--spacing-10) * 4)'),
    birth: fixed('calc(var(--spacing-10) * 3)'),
    roles: fixed('calc(var(--spacing-10) * 4)'),
    actions: fixed('calc(var(--spacing-10) * 5)'),
}

export default function ClubMembers({ clubId, onSessionLost }) {
    const [search, setSearch] = useState('')
    const [query, setQuery] = useState('')
    const [load, setLoad] = useState({ status: 'loading', list: [] })
    const [notice, setNotice] = useState(null) // { status, title }
    const [editing, setEditing] = useState(null) // { member } or { member: null } to add
    const [confirming, setConfirming] = useState(null) // { kind: 'refuse' | 'remove', member }
    const [busyId, setBusyId] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS)
        return () => clearTimeout(timer)
    }, [search])

    // Only the latest list is shown: an answer to a search the admin has
    // since typed past is dropped.
    const latest = useRef(0)
    const reload = useCallback(async () => {
        const call = ++latest.current
        setLoad(current => ({ ...current, status: current.status === 'ready' ? 'refreshing' : 'loading' }))
        let answer
        try {
            answer = await listMembers(clubId, query)
        } catch (e) {
            answer = { status: 0 }
        }
        if (call !== latest.current) return
        if (answer.status === 200 && answer.json) {
            setLoad({ status: 'ready', list: answer.json.list || [] })
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setLoad({ status: 'error', list: [], title: memberFailureText('list', answer.status) })
        }
    }, [clubId, query, onSessionLost])

    useEffect(() => {
        reload()
    }, [reload])

    // Every change: one call, then the list again. Returns whether it worked.
    const act = async (action, id, call, done) => {
        setBusyId(id)
        setNotice(null)
        let answer
        try {
            answer = await call()
        } catch (e) {
            answer = { status: 0 }
        }
        setBusyId('')
        if (answer.status === 200) {
            if (done) setNotice({ status: 'success', title: done })
            reload()
            return true
        }
        if (answer.status === 401) {
            onSessionLost()
            return false
        }
        setNotice({ status: 'error', title: memberFailureText(action, answer.status) })
        return false
    }

    const accept = request =>
        act('validate', request.id, () => validateMember(request.id), `${memberName(request)} est maintenant adhérent du club.`)

    const confirm = async () => {
        const { kind, member } = confirming
        const done = kind === 'refuse'
            ? `La demande de ${memberName(member)} a été refusée.`
            : `${memberName(member)} a été retiré des adhérents.`
        await act(kind === 'refuse' ? 'refuse' : 'delete', member.id, () => deleteMember(member.id), done)
        setConfirming(null)
    }

    const { pending, members } = useMemo(() => splitMembers(load.list), [load.list])

    const header = (
        <Stack direction="horizontal" gap={4} vAlign="end" hAlign="between" wrap="wrap">
            <Stack gap={1}>
                <Heading level={2}>Adhérents</Heading>
                <Text color="secondary">Les demandes des joueurs qui veulent rejoindre le club, et les adhérents inscrits.</Text>
            </Stack>
            <Stack direction="horizontal" gap={3} vAlign="center" wrap="wrap">
                {load.status === 'refreshing' && <Spinner size="sm" label="Mise à jour" />}
                <TextInput
                    label="Rechercher un adhérent"
                    isLabelHidden
                    startIcon="search"
                    placeholder="Rechercher par nom, e-mail, téléphone"
                    hasClear
                    value={search}
                    onChange={setSearch}
                    width={320}
                />
                <Button label="Ajouter un adhérent" variant="primary" onClick={() => setEditing({ member: null })} />
            </Stack>
        </Stack>
    )

    let body
    if (load.status === 'loading') {
        body = (
            <Center axis="both" padding={10}>
                <Spinner label="Chargement des adhérents" />
            </Center>
        )
    } else if (load.status === 'error') {
        body = (
            <Stack gap={3} hAlign="start">
                <Banner status="error" title={load.title} />
                <Button label="Réessayer" variant="secondary" onClick={reload} />
            </Stack>
        )
    } else if (pending.length === 0 && members.length === 0) {
        body = query
            ? (
                <EmptyState
                    title="Aucun résultat"
                    description={`Aucun adhérent ni aucune demande ne correspond à « ${query} ».`}
                    headingLevel={3}
                    actions={<Button label="Effacer la recherche" variant="secondary" onClick={() => setSearch('')} />}
                />
            )
            : (
                <EmptyState
                    title="Aucun adhérent"
                    description="Ce club n'a encore aucun adhérent ni aucune demande d'adhésion."
                    headingLevel={3}
                    actions={<Button label="Ajouter un adhérent" variant="secondary" onClick={() => setEditing({ member: null })} />}
                />
            )
    } else {
        body = (
            <Stack gap={8}>
                {pending.length > 0 && (
                    <Stack gap={3} paddingBlockEnd={4}>
                        <Heading level={3}>{pendingTitle(pending.length)}</Heading>
                        <PendingTable
                            requests={pending}
                            busyId={busyId}
                            onAccept={accept}
                            onRefuse={request => setConfirming({ kind: 'refuse', member: request })}
                        />
                    </Stack>
                )}
                <Stack gap={3}>
                    <Heading level={3}>{membersTitle(members.length)}</Heading>
                    {members.length > 0
                        ? (
                            <MembersTable
                                members={members}
                                busyId={busyId}
                                onEdit={member => setEditing({ member })}
                                onRemove={member => setConfirming({ kind: 'remove', member })}
                            />
                        )
                        : (
                            <Text color="secondary">
                                {query ? `Aucun adhérent ne correspond à « ${query} ».` : "Aucun adhérent inscrit pour l'instant."}
                            </Text>
                        )}
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack gap={6}>
            {header}
            {notice && (
                <Banner
                    status={notice.status}
                    title={notice.title}
                    isDismissable
                    dismissLabel="Masquer le message"
                    onDismiss={() => setNotice(null)}
                />
            )}
            {body}
            {editing && (
                <MemberDialog
                    clubId={clubId}
                    member={editing.member}
                    onClose={() => setEditing(null)}
                    onSaved={text => {
                        setEditing(null)
                        setNotice({ status: 'success', title: text })
                        reload()
                    }}
                    onSessionLost={onSessionLost}
                />
            )}
            <AlertDialog
                isOpen={!!confirming}
                onOpenChange={open => !open && setConfirming(null)}
                title={confirming && confirming.kind === 'refuse' ? 'Refuser la demande ?' : 'Retirer cet adhérent ?'}
                description={confirming
                    ? (confirming.kind === 'refuse' ? refuseQuestion(confirming.member) : removeQuestion(confirming.member))
                    : ''}
                actionLabel={confirming && confirming.kind === 'refuse' ? 'Refuser' : 'Retirer'}
                cancelLabel="Annuler"
                isActionLoading={!!busyId}
                onAction={confirm}
                width={440}
            />
        </Stack>
    )
}

ClubMembers.propTypes = {
    clubId: PropTypes.string.isRequired,
    // Called when a console call answers 401: the session is gone.
    onSessionLost: PropTypes.func.isRequired,
}

function Contact({ member }) {
    return (
        <Stack gap={0.5}>
            <Text weight="medium">{memberName(member)}</Text>
            {!member.user_id && <Text type="supporting" color="secondary">Sans compte Padelr</Text>}
        </Stack>
    )
}

Contact.propTypes = {
    member: PropTypes.object.isRequired,
}

function Dash({ text }) {
    return text ? <Text hasTabularNumbers>{text}</Text> : <Text color="secondary">—</Text>
}

Dash.propTypes = {
    text: PropTypes.string,
}

// The requests to join, each with what the player sent and the roster entry
// it will be merged into when there is one.
function PendingTable({ requests, busyId, onAccept, onRefuse }) {
    return (
        <Table density="compact" dividers="rows" verticalAlign="middle">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>Joueur</TableHeaderCell>
                    <TableHeaderCell style={columns.email}>E-mail</TableHeaderCell>
                    <TableHeaderCell style={columns.phone}>Téléphone</TableHeaderCell>
                    <TableHeaderCell style={columns.birth}>Naissance</TableHeaderCell>
                    <TableHeaderCell style={columns.actions}><VisuallyHidden>Actions</VisuallyHidden></TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {requests.map(request => {
                    const match = matchingMemberText(request)
                    return (
                        <TableRow key={request.id}>
                            <TableCell>
                                <Stack gap={0.5}>
                                    <Text weight="medium">{memberName(request)}</Text>
                                    {match && <Text type="supporting" color="secondary">{match}</Text>}
                                </Stack>
                            </TableCell>
                            <TableCell style={columns.email}><Dash text={request.email} /></TableCell>
                            <TableCell style={columns.phone}><Dash text={request.phone} /></TableCell>
                            <TableCell style={columns.birth}><Dash text={birthDateText(request.birth_date)} /></TableCell>
                            <TableCell style={columns.actions}>
                                <Stack direction="horizontal" gap={2} hAlign="end">
                                    <Button
                                        label="Accepter"
                                        variant="primary"
                                        size="sm"
                                        isLoading={busyId === request.id}
                                        isDisabled={!!busyId && busyId !== request.id}
                                        onClick={() => onAccept(request)}
                                    />
                                    <Button
                                        label="Refuser"
                                        variant="secondary"
                                        size="sm"
                                        isDisabled={!!busyId}
                                        onClick={() => onRefuse(request)}
                                    />
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}

PendingTable.propTypes = {
    requests: PropTypes.arrayOf(PropTypes.object).isRequired,
    busyId: PropTypes.string.isRequired,
    onAccept: PropTypes.func.isRequired,
    onRefuse: PropTypes.func.isRequired,
}

function MembersTable({ members, busyId, onEdit, onRemove }) {
    return (
        <Table density="compact" dividers="rows" verticalAlign="middle">
            <TableHeader>
                <TableRow>
                    <TableHeaderCell>Nom</TableHeaderCell>
                    <TableHeaderCell style={columns.email}>E-mail</TableHeaderCell>
                    <TableHeaderCell style={columns.phone}>Téléphone</TableHeaderCell>
                    <TableHeaderCell style={columns.birth}>Naissance</TableHeaderCell>
                    <TableHeaderCell style={columns.roles}>Rôles</TableHeaderCell>
                    <TableHeaderCell style={columns.actions}><VisuallyHidden>Actions</VisuallyHidden></TableHeaderCell>
                </TableRow>
            </TableHeader>
            <TableBody>
                {members.map(member => (
                    <TableRow key={member.id}>
                        <TableCell><Contact member={member} /></TableCell>
                        <TableCell style={columns.email}><Dash text={member.email} /></TableCell>
                        <TableCell style={columns.phone}><Dash text={member.phone} /></TableCell>
                        <TableCell style={columns.birth}><Dash text={birthDateText(member.birth_date)} /></TableCell>
                        <TableCell style={columns.roles}>
                            <Stack direction="horizontal" gap={1} wrap="wrap">
                                {memberRoles(member).map(role => (
                                    <StackItem key={role.label} size="static">
                                        <Token label={role.label} color={role.color} size="sm" />
                                    </StackItem>
                                ))}
                            </Stack>
                        </TableCell>
                        <TableCell style={columns.actions}>
                            <Stack direction="horizontal" gap={2} hAlign="end">
                                <Button label="Modifier" variant="secondary" size="sm" isDisabled={!!busyId} onClick={() => onEdit(member)} />
                                <Button label="Retirer" variant="secondary" size="sm" isDisabled={!!busyId} onClick={() => onRemove(member)} />
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    )
}

MembersTable.propTypes = {
    members: PropTypes.arrayOf(PropTypes.object).isRequired,
    busyId: PropTypes.string.isRequired,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
}

// Adds a member (member null) or edits one. A new member is either a Padelr
// player picked from a search - linked to their account, whose email the
// server uses - or somebody typed in with no account. Either starts with no
// role; an edit can make one a coach, and one with an account an admin.
function MemberDialog({ clubId, member, onClose, onSaved, onSessionLost }) {
    const isNew = !member
    const [draft, setDraft] = useState(() => memberDraft(member))
    const [mode, setMode] = useState('player')
    const [player, setPlayer] = useState(null)
    const [isSaving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [isTouched, setTouched] = useState(false)

    const isPicking = isNew && mode === 'player'
    const picked = isPicking ? player : null
    const today = todayParts()
    const errors = memberDraftErrors(draft, today, picked)
    const isValid = Object.keys(errors).length === 0 && (!isPicking || !!player)

    // Each mode starts from an empty form: a name picked from an account is
    // not one to carry over to somebody typed in, nor the other way round.
    const switchMode = value => {
        setMode(value)
        setPlayer(null)
        setDraft(memberDraft(null))
        setError('')
        setTouched(false)
    }

    const pick = found => {
        setPlayer(found)
        setError('')
        setDraft(current => ({ ...current, ...playerDraft(found) }))
    }
    const change = patch => setDraft(current => ({ ...current, ...patch }))
    const fieldStatus = key => (isTouched && errors[key] ? { type: 'error', message: errors[key] } : undefined)

    const save = async () => {
        setTouched(true)
        if (isPicking && !player) setError('Choisissez un joueur, ou ajoutez-le sans compte.')
        if (!isValid) return
        setSaving(true)
        setError('')
        let answer
        try {
            answer = isNew
                ? await createMember(memberToCreate(clubId, draft, picked))
                : await updateMember(memberToUpdate(member, draft))
        } catch (e) {
            answer = { status: 0 }
        }
        setSaving(false)
        if (answer.status === 200) {
            const name = memberName(answer.json && answer.json.clubuser ? answer.json.clubuser : draft)
            onSaved(isNew ? `${name} a été ajouté aux adhérents.` : `La fiche de ${name} a été enregistrée.`)
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setError(memberFailureText(isNew ? (picked ? 'link' : 'create') : 'update', answer.status))
        }
    }

    const note = isNew ? '' : adminNote(member, draft.is_admin)

    return (
        <Dialog isOpen onOpenChange={open => !open && onClose()} purpose="form" width={520}>
            <Layout
                header={
                    <DialogHeader
                        title={isNew ? 'Ajouter un adhérent' : 'Modifier l\'adhérent'}
                        subtitle={isNew ? addSubtitle(mode) : memberName(member)}
                        onOpenChange={() => onClose()}
                    />
                }
                content={
                    <LayoutContent>
                        <Stack gap={4}>
                            {error && <Banner status="error" title={error} />}
                            {isNew && (
                                <RadioList
                                    label="Qui ajouter"
                                    value={mode}
                                    onChange={switchMode}
                                    orientation="horizontal"
                                >
                                    {ADD_MODES.map(m => <RadioListItem key={m.value} value={m.value} label={m.label} />)}
                                </RadioList>
                            )}
                            {isPicking && !player && (
                                <PlayerPicker clubId={clubId} onPick={pick} onSessionLost={onSessionLost} />
                            )}
                            {isPicking && player && (
                                <List hasDividers density="compact">
                                    <ListItem
                                        label={player.full_name || player.username}
                                        description={playerLine(player) || undefined}
                                        endContent={<Button label="Changer" variant="secondary" size="sm" onClick={() => setPlayer(null)} />}
                                    />
                                </List>
                            )}
                            {(!isPicking || player) && (<>
                            <Stack direction="horizontal" gap={3}>
                                <StackItem size="fill">
                                    <TextInput
                                        label="Prénom"
                                        value={draft.first_name}
                                        onChange={value => change({ first_name: value })}
                                        autoComplete="off"
                                        hasAutoFocus
                                        width="100%"
                                        status={fieldStatus('name')}
                                    />
                                </StackItem>
                                <StackItem size="fill">
                                    <TextInput
                                        label="Nom"
                                        value={draft.last_name}
                                        onChange={value => change({ last_name: value })}
                                        autoComplete="off"
                                        width="100%"
                                    />
                                </StackItem>
                            </Stack>
                            <DateInput
                                label="Date de naissance"
                                isOptional
                                presentation="native"
                                format={birthDateText}
                                placeholder="jj/mm/aaaa"
                                value={draft.birth_date || undefined}
                                max={isoDay(today)}
                                min="1900-01-02"
                                hasClear
                                onChange={value => change({ birth_date: value || '' })}
                                status={fieldStatus('birth_date')}
                                width={240}
                            />
                            {picked ? (
                                <Text type="supporting" color="secondary">{ACCOUNT_EMAIL_NOTE}</Text>
                            ) : (
                                <TextInput
                                    label="E-mail"
                                    type="email"
                                    isOptional
                                    value={draft.email}
                                    onChange={value => change({ email: value })}
                                    autoComplete="off"
                                    status={fieldStatus('email')}
                                />
                            )}
                            <TextInput
                                label="Téléphone"
                                isOptional
                                value={draft.phone}
                                onChange={value => change({ phone: value })}
                                autoComplete="off"
                            />
                            </>)}
                            {!isNew && (
                                <Stack gap={3}>
                                    <Heading level={4}>Rôle</Heading>
                                    <Switch
                                        label="Coach"
                                        description="Peut publier des créneaux de cours dans ce club."
                                        value={draft.is_coach}
                                        onChange={value => change({ is_coach: value })}
                                    />
                                    <Switch
                                        label="Personnel"
                                        description="Travaille pour le club : entretien, accueil… Repéré dans l'onglet Occupation."
                                        value={draft.is_staff}
                                        onChange={value => change({ is_staff: value })}
                                    />
                                    <Switch
                                        label="Administrateur"
                                        value={canBeAdmin(member) && draft.is_admin}
                                        isDisabled={!canBeAdmin(member)}
                                        description={note || undefined}
                                        onChange={value => change({ is_admin: value })}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </LayoutContent>
                }
                footer={
                    <LayoutFooter>
                        <Stack direction="horizontal" gap={2} hAlign="end">
                            <Button label="Annuler" variant="secondary" onClick={onClose} />
                            <Button
                                label={isNew ? 'Ajouter' : 'Enregistrer'}
                                variant="primary"
                                isDisabled={isTouched && !isValid}
                                isLoading={isSaving}
                                onClick={save}
                            />
                        </Stack>
                    </LayoutFooter>
                }
            />
        </Dialog>
    )
}

MemberDialog.propTypes = {
    clubId: PropTypes.string.isRequired,
    member: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

function addSubtitle(mode) {
    return mode === 'player'
        ? 'Un joueur qui a déjà un compte Padelr : sa fiche est reliée à son compte, et vous ne complétez que ce qui manque.'
        : 'Un adhérent sans compte Padelr. Le joueur pourra relier sa fiche en demandant à rejoindre le club avec la même adresse e-mail.'
}

// Finds a Padelr player by name, a moment after the admin stops typing, and
// hands the one picked to the dialog. Players already on the roster are shown
// but cannot be picked: the server would refuse them.
function PlayerPicker({ clubId, onPick, onSessionLost }) {
    const [search, setSearch] = useState('')
    const [found, setFound] = useState({ status: 'idle', list: [] })

    const latest = useRef(0)
    useEffect(() => {
        const query = search.trim()
        if (!query) {
            latest.current++
            setFound({ status: 'idle', list: [] })
            return undefined
        }
        const timer = setTimeout(async () => {
            const call = ++latest.current
            setFound(current => ({ ...current, status: 'loading' }))
            let answer
            try {
                answer = await searchPlayers(clubId, query)
            } catch (e) {
                answer = { status: 0 }
            }
            if (call !== latest.current) return
            if (answer.status === 200 && answer.json) {
                setFound({ status: 'ready', list: answer.json.list || [] })
            } else if (answer.status === 401) {
                onSessionLost()
            } else {
                setFound({ status: 'error', list: [] })
            }
        }, SEARCH_DELAY_MS)
        return () => clearTimeout(timer)
    }, [clubId, search, onSessionLost])

    return (
        <Stack gap={2}>
            <TextInput
                label="Joueur Padelr"
                startIcon="search"
                placeholder="Nom ou pseudo"
                value={search}
                onChange={setSearch}
                autoComplete="off"
                hasAutoFocus
                width="100%"
            />
            {found.status === 'loading' && found.list.length === 0 && <Spinner size="sm" label="Recherche" />}
            {found.status === 'error' && <Banner status="error" title="La recherche a échoué. Réessayez." />}
            {found.status === 'ready' && found.list.length === 0 && (
                <Text type="supporting" color="secondary">Aucun joueur ne correspond. Vous pouvez l'ajouter sans compte.</Text>
            )}
            {found.list.length > 0 && (
                <List hasDividers density="compact">
                    {found.list.map(p => (
                        <ListItem
                            key={p.id}
                            label={p.full_name || p.username}
                            description={playerLine(p) || undefined}
                            isDisabled={p.is_member}
                            onClick={p.is_member ? undefined : () => onPick(p)}
                        />
                    ))}
                </List>
            )}
        </Stack>
    )
}

PlayerPicker.propTypes = {
    clubId: PropTypes.string.isRequired,
    onPick: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}
